import { useCallback, useEffect, useRef, useState } from "react";
import { fetchList } from "@/lib/api";

export const PAGE_SIZE = 24;

// Debounce a fast-changing value (e.g. a search input) before it hits the API.
export const useDebouncedValue = (value, delay = 350) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
};

// Paginated public list: first page on filter change, "load more" appends the next one.
export const usePagedList = (path, params, pageSize = PAGE_SIZE) => {
  const key = JSON.stringify(params);
  const reqId = useRef(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    const id = ++reqId.current;
    setLoading(true);
    fetchList(path, { ...JSON.parse(key), limit: pageSize, skip: 0 })
      .then((data) => {
        if (id !== reqId.current) return;
        setItems(data || []);
        setHasMore((data || []).length === pageSize);
      })
      .catch(() => { if (id === reqId.current) { setItems([]); setHasMore(false); } })
      .finally(() => { if (id === reqId.current) setLoading(false); });
  }, [path, key, pageSize]);

  const loadMore = useCallback(() => {
    const id = reqId.current;
    setLoadingMore(true);
    fetchList(path, { ...JSON.parse(key), limit: pageSize, skip: items.length })
      .then((data) => {
        if (id !== reqId.current) return;
        const page = data || [];
        setItems((prev) => {
          const seen = new Set(prev.map((x) => x.id));
          return [...prev, ...page.filter((x) => !seen.has(x.id))];
        });
        setHasMore(page.length === pageSize);
      })
      .catch(() => {})
      .finally(() => setLoadingMore(false));
  }, [path, key, pageSize, items.length]);

  return { items, loading, loadingMore, hasMore, loadMore };
};
